import { useCallback, useEffect, useRef, useState } from 'react';

import { CaveBagDrawer } from './CaveBagDrawer';
import { CaveDecor } from './CaveDecor';
import { CharacterAuraFX } from './CharacterAuraFX';
import { CorridorRock } from './CorridorRock';
import { DepthTracker } from './DepthTracker';
import { HazardOverlay } from './HazardOverlay';
import { PauseMenu } from './PauseMenu';
import { MinerBack } from './MinerBack';
import { Icon } from '@/components/ui/Icon';
import { CaveEnterCinematic } from './CaveEnterCinematic';
import { CaveExitCinematic } from './CaveExitCinematic';
import { WallSearch, WallSearchCard } from './WallSearch';
import { RockDebris } from './RockDebris';
import { RockField } from './RockField';
import { RockFocus } from './RockFocus';
import { SoundSettingsModal } from '@/components/ui/SoundSettingsModal';
import { ThroatRings } from './ThroatRings';
import { WallRocks } from './WallRocks';
import { TorchLight } from './TorchLight';
import { sound, type MusicTheme } from '@/game/audio/sound';
import { CONFIG } from '@/game/data';
import { useRun, selectTorchPct } from '@/store/run';
import { selectBrightness, useSettings } from '@/store/settings';
import { shade, voidRgb, voidRgba } from '@/game/engine/lighting';
import { useSession, selectLightRadius } from '@/store/session';

/**
 * The cave — looking INTO it, per prototype.png panels 3, 4, 5–7 and 9.
 *
 * Replaces the side-scrolling Tunnel. The corridor recedes to a vanishing point,
 * walls curve in, and walking forward brings everything toward the camera instead
 * of sliding the world sideways.
 *
 * Layout follows the mock exactly:
 *   torch power bar      top
 *   depth / limit        below it
 *   cave viewport        the rest
 *   ◀  ▲Move Forward  ▶  bottom
 */
/**
 * How deep the entrance arch stays visible, in metres.
 *
 * Short on purpose: the fantasy is an endless descent, so the surface should be
 * gone almost immediately. Long enough that a player who walks a couple of steps
 * and looks back understands what they just left.
 */
const ENTRANCE_FADE_M = 14;

export function Corridor({ onSurface, onMaps }: {
  onSurface: () => void;
  /** Open the map screen without ending the run. */
  onMaps: () => void;
}) {
  const phase = useRun((s) => s.phase);
  const depth = useRun((s) => s.depth);
  const zone = useRun((s) => s.zone);
  /* A display preference, not game state — see store/settings.ts. It reaches how the
     cave is painted and nothing else, so no reward, gate or balance can see it. */
  const brightness = useSettings(selectBrightness);
  /* The same "dark" TorchLight paints with: this cave's fog colour, floored off
     black, at an alpha the brightness setting divides and DARK_CEILING caps. */
  const caveDark = (alpha: number) =>
    voidRgba(voidRgb(zone.fog), shade(alpha, brightness));
  /* Falls back to the old hardcoded amber so an older server — one that predates
     `ZoneDTO.glow` — still shows embers instead of invisible, colourless dots. */
  const emberGlow = zone.glow ?? '#F5A623';
  const rock = useRun((s) => s.rock);
  const spawns = useRun((s) => s.spawns);
  const blocker = useRun((s) => s.blocker);
  const torchPct = useRun(selectTorchPct);
  const crackStage = useRun((s) => s.crackStage);
  const throttled = useRun((s) => s.throttled);
  const notice = useRun((s) => s.notice);
  const dismissNotice = useRun((s) => s.dismissNotice);

  const enter = useRun((s) => s.enter);
  const step = useRun((s) => s.step);
  const stepBack = useRun((s) => s.stepBack);
  /* swing() itself is no longer called here — RockFocus owns the call, so the
     corridor cannot double-hit. Only the feedback stays (swingFx). */
  const exit = useRun((s) => s.exit);
  const clearBlocker = useRun((s) => s.clearBlocker);

  const lightPx = useSession(selectLightRadius);
  const lampEquipment = useSession((s) => s.equipment['lamp']);
  const axeEquipment = useSession((s) => s.equipment['axe']);
  const lampLevel = lampEquipment?.level ?? 1;
  const lampName = lampEquipment?.name ?? `Lamp Lvl ${lampLevel}`;
  const axeLevel = axeEquipment?.level ?? 1;
  const axeName = axeEquipment?.name ?? `Axe Lvl ${axeLevel}`;
  const maxDepth = lampEquipment?.stats.max_depth_m;
  const energy = useSession((s) => s.energy);

  const [paused, setPaused] = useState(false);
  const [bagOpen, setBagOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  /* Purely presentational: which lens the player is looking through. The run
     itself is unchanged, so this never needs to survive a reload. */
  const [focused, setFocused] = useState(false);
  const [walking, setWalking] = useState(false);
  const [swinging, setSwinging] = useState(false);
  const [shake, setShake] = useState(false);
  const [view, setView] = useState({ width: 390, height: 560 });
  const boxRef = useRef<HTMLDivElement | null>(null);
  const holdRef = useRef<number | null>(null);

  /* The projection needs real pixels, so measure rather than assume. */
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setView({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /*
   * Surfacing, in one place.
   *
   * Both routes out — the "torch is dead, go back" card and the pause menu's SURFACE
   * tile — used to call `void exit().then(onSurface)` independently, which is two
   * copies of the run's ending and exactly the shape of thing that drifts. `leaving`
   * raises the walk-out over the cave; `exited` says the server has answered and the
   * shot may hand over to the haul.
   */
  const [leaving, setLeaving] = useState(false);
  const [exited, setExited] = useState(false);
  const summary = useRun((s) => s.summary);
  const dismissSummary = useRun((s) => s.dismissSummary);

  const surface = useCallback(() => {
    setPaused(false);
    setLeaving(true);
    /* `exit()` never rejects — it swallows a failed call and resets the run anyway,
       because the player asked to leave and the cave is coming down either way. So
       this only has to record that the wait is over. */
    void exit().finally(() => setExited(true));
  }, [exit]);

  /*
   * Open a run when there is not one.
   *
   * `!leaving` is not a nicety. `exit()` sets the phase to 'idle' and this component
   * stays mounted for the length of the walk-out, so without the guard the very act of
   * surfacing immediately opened a *new* descent underneath the cinematic: the player
   * watched themselves leave the cave while the game quietly put them back in one at
   * 0 m with a full torch.
   */
  useEffect(() => {
    if (phase === 'idle' && !leaving) void enter();
  }, [phase, enter, leaving]);

  /*
   * The cave has a soundtrack, and until now nothing ever started it.
   *
   * `sound.ts` carries eight themes and ten effects — a few hundred lines of Web
   * Audio synthesis — and `playTheme` had exactly one caller in the whole app:
   * the preview button inside the settings modal. So the game was silent in the
   * menu, silent in the cave, and made exactly one sound in play (a hit).
   *
   * `ensureTheme` rather than `playTheme` because this effect re-runs whenever
   * the run opens or closes, and `playTheme` restarts from bar one every call —
   * which is right for a preview and a stutter here. Autoplay policy is satisfied
   * by construction: you can only get here by tapping "Enter the cave", and the
   * engine unlocks its context on the first pointer event.
   */
  useEffect(() => {
    if (phase === 'idle') return;
    const caveThemeMap: Record<string, MusicTheme> = {
      glass_reef: 'crystal_cave',
      emberfall: 'lava_cave',
      hollow_shrine: 'legendary_room',
      abandoned_mine: depth >= 500 ? 'deep_cave' : 'mining',
    };
    const caveKey = zone?.cave ?? 'abandoned_mine';
    const theme: MusicTheme = caveThemeMap[caveKey] ?? (depth >= 500 ? 'deep_cave' : 'mining');
    sound.ensureTheme(theme);
    return () => sound.stopBgm();
  }, [phase, zone?.cave, depth]);

  /* The walk in. Covers `POST /cave/enter` and lifts the moment the run is open —
     the cinematic is not allowed to become the reason the player is waiting, so it
     tracks real readiness rather than a fixed duration.

     Decided at mount from whether a run is already open, not simply `true`. The old
     comment claimed it was "shown once per mount" as though a mount only happened at
     the start of a descent — but this component unmounts whenever the player opens a
     meta screen over the cave, so stepping out to the map screen mid-run and coming
     back played the full walk-in again, for a wait that was not happening. */
  const [entering, setEntering] = useState(() => phase === 'idle');

  /* Ready means the wait is over, which is two things and not one: the run opened,
     *or* `enter()` failed and left a notice to read. Without the second case a
     failed enter would sit behind the cinematic forever — nothing after that would
     ever move `phase` off 'idle', so the player would watch a lantern swing instead
     of being told the server said no. */
  const runOpen = phase !== 'idle' || notice !== null;

  const doStep = useCallback(() => {
    setWalking(true);
    void step().finally(() => setTimeout(() => setWalking(false), CONFIG.stepMs));
  }, [step]);

  const doStepBack = useCallback(() => {
    setWalking(true);
    void stepBack().finally(() => setTimeout(() => setWalking(false), CONFIG.stepMs));
  }, [stepBack]);

  /* Feedback only — RockFocus makes the actual swing() call.
   *
   * Splitting them stops the one bug this refactor could easily have introduced:
   * if this still called swing() and the focus view called it too, every tap would
   * have sent two hits and burned double the fuel. */
  const swingFx = useCallback(() => {
    setSwinging(true);
    setShake(true);
    setTimeout(() => setShake(false), 180);
    setTimeout(() => setSwinging(false), 340);
  }, []);

  const startHold = useCallback(() => {
    doStep();
    holdRef.current = window.setInterval(doStep, CONFIG.stepMs + 40);
  }, [doStep]);

  const stopHold = useCallback(() => {
    if (holdRef.current !== null) {
      clearInterval(holdRef.current);
      holdRef.current = null;
    }
  }, []);

  useEffect(() => stopHold, [stopHold]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'w') { e.preventDefault(); doStep(); }
      if (e.key === 'ArrowDown' || e.key === 's') { e.preventDefault(); doStepBack(); }
      /* Space opens the view rather than swinging blind, matching both buttons. */
      if (e.key === ' ' && rock && !rock.locked) { e.preventDefault(); setFocused(true); }
      if (e.key === 'Escape' || e.key === 'p') { e.preventDefault(); setPaused(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doStep, doStepBack, rock]);

  const endless = maxDepth == null || maxDepth < 0;
  const capLabel = endless ? 'endless' : `${maxDepth}m`;
  const lampBlocked = blocker?.code === 'LAMP_LEVEL_TOO_LOW';
  const torchDead = torchPct <= 0;

  /* Only a rock you CAN break stops the walk.
  
     A locked rock — one your axe cannot touch — is meant to be walked past;
     the server expires it and lets you through. Treating every rock as a
     blocker meant the first Granite at axe Lv1 ended the run with a dead
     button and no explanation. */
  const breakableInPath = rock != null && !rock.locked;
  const lockedInPath = rock != null && rock.locked;

  /* The lamp's depth cap is a hard wall, not a transient error. Leaving MOVE
     FORWARD enabled here let the player tap it over and over, collecting a
     LAMP_LEVEL_TOO_LOW refusal each time — the server says no, the UI says
     nothing, and it reads as a broken button rather than a limit reached. */
  const atLampCap = !endless && maxDepth != null && depth >= maxDepth;
  const cannotWalk = torchDead || breakableInPath || atLampCap || phase === 'idle';

  /* Until the server sends a depth per spawn (CAVE_VIEW phase 3), the active rock
     sits at the near end of reach — where the mock puts a blocker you can hit. */
  const rockDepth = rock?.locked ? 3.2 : 2.0;

  return (
    <div className="relative flex h-full flex-col">
      {entering && (
        <CaveEnterCinematic ready={runOpen} onDone={() => setEntering(false)} />
      )}

      {/* The walk out, and what the descent came to. Covers `POST /cave/exit` the way
          the entrance covers `/cave/enter`, then holds the haul until it is read —
          before this, tapping SURFACE unmounted the cave mid-frame and the run simply
          stopped existing, silently. */}
      {leaving && (
        <CaveExitCinematic
          summary={summary}
          ready={exited}
          onDone={() => { dismissSummary(); onSurface(); }}
        />
      )}
      {/* ── torch power, mock panel 3 ── */}
      <div className="shrink-0 border-b border-[var(--hairline)] bg-black/40 px-2.5 py-2">
        <div className="flex items-center gap-2">
          <Icon name="torch" size={16} className="text-torch-core" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="font-pixel text-[6.5px] tracking-wide text-ink-dim">TORCH POWER</p>
              {lampLevel >= 3 && (
                <span className="rounded bg-[var(--ok)]/20 px-1 py-0.2 font-pixel text-[5.5px] text-[var(--ok)]">
                  OIL SAVER (−15%)
                </span>
              )}
            </div>
            <span className="mt-1 block h-2 overflow-hidden rounded-full bg-white/10">
              <i className="block h-full rounded-full transition-[width] duration-300"
                 style={{ width: `${torchPct}%`,
                          background: 'linear-gradient(90deg,var(--torch-deep),var(--torch-core))' }} />
            </span>
          </div>
          <span className="font-pixel text-[9px] text-torch-core tabular">{Math.round(torchPct)}%</span>
          {/* Energy sits with the torch because they are the same kind of fact —
              the two meters a run spends. It used to hang off a flex-1 spacer at
              the end of the wrapping badge row, which pushed it onto a line of its
              own and charged the cave 40px of height for one number. */}
          <span className="flex shrink-0 items-center gap-1 font-bold text-ink-dim tabular">
            <Icon name="energy" size={12} className="text-amber-400" />
            <span>{energy.current}/{energy.max}</span>
          </span>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px]">
          <span className="font-pixel text-[9px] text-torch-core tabular">DEPTH {depth}m</span>
          <span className="h-3 w-px bg-[var(--hairline)]" aria-hidden />

          {/* Active Lamp Level & Name Badge */}
          <span className="flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 border border-amber-500/30 font-pixel text-[7px] text-amber-300">
            <Icon name="lamp" size={10} className="text-amber-400" />
            {/* The name is a shop fact; in the cave the level is what governs.
                Carrying "(Candle)" and "(Wood Axe)" wrapped this row and cost the
                cave a whole line of height. Still announced, just not drawn. */}
            <span>LVL {lampLevel}</span>
            <span className="sr-only">{lampName}</span>
          </span>

          {/* Active Axe Level & Name Badge */}
          <span className="flex items-center gap-1 rounded bg-orange-500/20 px-1.5 py-0.5 border border-orange-500/30 font-pixel text-[7px] text-orange-300">
            <Icon name="axe" size={10} className="text-orange-400" />
            <span>LVL {axeLevel}</span>
            <span className="sr-only">{axeName}</span>
          </span>

          {/* In-Cave Quick Bag Drawer Trigger */}
          <button
            type="button"
            onClick={() => setBagOpen(true)}
            className="flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 border border-amber-500/30 font-pixel text-[7px] text-amber-300 hover:bg-amber-500/30 active:scale-95 transition-all cursor-pointer"
          >
            <Icon name="bag" size={10} className="text-amber-300" />
            <span>BAG</span>
          </button>

          {/* Sound Controls Modal Trigger */}
          <button
            type="button"
            onClick={() => setSoundOpen(true)}
            className="flex items-center gap-1 rounded bg-slate-800/60 px-1.5 py-0.5 border border-white/20 font-pixel text-[7px] text-ink-bright hover:bg-white/10 active:scale-95 transition-all cursor-pointer"
          >
            <Icon name="sound" size={10} className="text-ink-bright" />
            <span>SOUND</span>
          </button>

        </div>
      </div>

      {/* ── Dynamic Cave Depth Corridor Progress Tracker Bar ── */}
      <DepthTracker depth={depth} maxDepth={maxDepth} />

      {/* ── the cave ── */}
      <div
        ref={boxRef}
        className={`relative min-h-0 flex-1 overflow-hidden ${shake ? 'animate-[shake_.18s_steps(3)]' : ''}`}
        style={{
          backgroundColor: zone.tint,
          /* Ceiling falloff, composited INTO the wall rather than laid over it.
             The torch is carried at floor level, so the roof should be the
             darkest surface in frame — instead the tiled wall made the upper
             third the brightest thing on screen and pulled the eye off the rock
             the player can actually hit. An overlay div could not fix it: this
             tile is the box's own background, so anything above it in the stack
             was fighting paint order rather than changing what was too bright.
             Multiplied as a second background layer, it darkens the tile itself.

             Painted in the CAVE's dark and through the player's brightness, exactly
             like every stop in TorchLight — this is the single largest dark area in
             frame, so leaving it as a hardcoded near-black would have meant the
             brightness slider visibly failing to touch the roof. */
          backgroundImage:
            `linear-gradient(180deg, ${caveDark(0.78)} 0%, ${caveDark(0.60)} 26%,`
            + ` ${caveDark(0.30)} 54%, rgba(255,255,255,0) 78%),`
            + ' url(/wall-tile.webp)',
          backgroundRepeat: 'no-repeat, repeat',
          backgroundSize: '100% 100%, 116px 116px',
          backgroundBlendMode: 'multiply, multiply',
        }}
      >
        {/* Atmospheric floating motes, tinted and styled by the active cave biome */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10 select-none">
          <span
            className={`absolute left-[15%] bottom-[10%] h-1.5 w-1.5 ${zone.cave === 'glass_reef' ? 'rotate-45 shadow-[0_0_6px_#7FD4FF]' : 'rounded-full'} blur-[0.5px] animate-[emberFloat_4.2s_ease-in-out_infinite]`}
            style={{ animationDelay: '0s', backgroundColor: emberGlow, opacity: 0.65 }}
          />
          <span
            className={`absolute left-[35%] bottom-[5%] h-2 w-2 ${zone.cave === 'glass_reef' ? 'rotate-45 shadow-[0_0_8px_#7FD4FF]' : 'rounded-full'} blur-[1px] animate-[emberFloat_5.6s_ease-in-out_infinite]`}
            style={{ animationDelay: '1.4s', backgroundColor: emberGlow, opacity: 0.55 }}
          />
          <span
            className={`absolute left-[65%] bottom-[12%] h-1.5 w-1.5 ${zone.cave === 'glass_reef' ? 'rotate-45' : 'rounded-full'} animate-[emberFloat_3.8s_ease-in-out_infinite]`}
            style={{ animationDelay: '0.7s', backgroundColor: emberGlow, opacity: 0.75 }}
          />
          <span
            className={`absolute left-[82%] bottom-[8%] h-2 w-2 ${zone.cave === 'glass_reef' ? 'rotate-45 shadow-[0_0_6px_#7FD4FF]' : 'rounded-full'} blur-[0.5px] animate-[emberFloat_4.8s_ease-in-out_infinite]`}
            style={{ animationDelay: '2.1s', backgroundColor: emberGlow, opacity: 0.6 }}
          />
          {zone.cave === 'emberfall' && (
            <>
              <span className="absolute left-[24%] bottom-[4%] h-1 w-1 rounded-full animate-[emberFloat_2.9s_ease-in-out_infinite]" style={{ animationDelay: '0.3s', backgroundColor: '#FF5722', opacity: 0.8 }} />
              <span className="absolute left-[78%] bottom-[15%] h-1 w-1 rounded-full animate-[emberFloat_3.2s_ease-in-out_infinite]" style={{ animationDelay: '1.8s', backgroundColor: '#FF9800', opacity: 0.7 }} />
            </>
          )}
          {zone.cave === 'glass_reef' && (
            <>
              <span className="absolute left-[28%] bottom-[18%] h-1 w-1 rotate-45 animate-[emberFloat_4.5s_ease-in-out_infinite]" style={{ animationDelay: '2.5s', backgroundColor: '#A5E6FF', opacity: 0.8 }} />
              <span className="absolute left-[72%] bottom-[6%] h-1.5 w-1.5 rotate-45 animate-[emberFloat_5.1s_ease-in-out_infinite]" style={{ animationDelay: '1.1s', backgroundColor: '#7FD4FF', opacity: 0.7 }} />
            </>
          )}
        </div>

        {/* Dynamic Cave Hazards & Animated Obstacle Events */}
        <HazardOverlay />

        {/* ── 3D Realistic Perspective Tunnel Corridor ── */}
        <div
          className="absolute inset-0 transition-transform duration-300 ease-out"
          style={{
            transformOrigin: '50% 46%',
            animation: walking ? 'tunnelWalk .42s ease-in-out infinite' : undefined,
          }}
        >
          <ThroatRings lightPx={lightPx} />

          {/* Character Active Special Skill Visual Aura FX */}
          <CharacterAuraFX />

          {/* Far-distance filler. Real rock is drawn by RockField in front of this;
              these only fill the fogged band past the lamp, where no spawn exists to
              draw and a bare tube reads as a corridor made of nothing. */}
          <WallRocks depth={depth} view={view} lightPx={lightPx} />
        </div>

        {/*
          * The rock the cave is made of — real server spawns, placed by the server,
          * tappable once they are in reach.
          *
          * `z-[4]` is load-bearing, not layout tidying. These buttons carried their
          * own `zIndex` of 900-980 and so painted *above* every layer of TorchLight:
          * the walls were lit and fogged by the torch and the rocks were not, which
          * is precisely why they read as sprites pasted onto the cave instead of
          * stone standing in it. Now they sit between the torch's two passes — over
          * the glow at z-3, under the multiply cone and vignette at z-5 — so the
          * cave's darkness falls on them without its bloom washing them flat. See
          * the note in TorchLight for why that split exists.
          *
          * The per-rock `zIndex` still orders them among themselves, inside the
          * stacking context this wrapper creates. The wrapper takes no pointer
          * events, so the corridor behind it is still reachable; the rocks
          * re-enable them on themselves.
          */}
        <div className="pointer-events-none absolute inset-0 z-[4]">
          {!focused && (
            <RockField
              spawns={spawns}
              depth={depth}
              view={view}
              lightPx={lightPx}
              activeUuid={rock?.spawn_uuid ?? null}
              onPick={() => setFocused(true)}
            />
          )}

          {/* The rock that just came out of the wall, still falling.
              Outside the `!focused` gate on purpose: the break is what closes the focus
              view, so a knock-out drawn only when unfocused would lose its first frames
              to the same render that dismissed the lens it was broken in. */}
          <RockDebris depth={depth} view={view} lightPx={lightPx} />

          {rock && !focused && (
            <CorridorRock
              rock={rock}
              depth={rockDepth}
              crackStage={crackStage}
              view={view}
              lightPx={lightPx}
              /* Opens the full-screen view rather than swinging here. The corridor
                 projects the rock to a few dozen pixels at distance, which is the
                 wrong place to ask for a precise repeated tap. */
              onSwing={() => setFocused(true)}
            />
          )}
        </div>

        {/*
          * The cave mouth. A rock arch over the first few metres, fading as you
          * walk in — the only moment in the game where the surface is still
          * visible, and the reason a run has a beginning rather than just a depth.
          *
          * Deliberately NOT drawn at the far end of the corridor: the player faces
          * into the dark and we see their back, so a mouth down there would read
          * as "there is another cave ahead", the opposite of the pitch.
          *
          * Hidden entirely past ENTRANCE_FADE_M so it costs nothing for the rest
          * of an endless descent.
          */}
        {depth < ENTRANCE_FADE_M && (
          <div
            className="pointer-events-none absolute inset-0 z-[7] bg-cover bg-center bg-no-repeat
                       transition-opacity duration-500"
            style={{
              backgroundImage: 'url(/cave-entrance.webp)',
              /* Lit by the torch, like everything else. This art is the first thing
                 every player sees — every run starts at 0 m — and untinted at .92 it
                 washed the opening frames pale grey and made the roof the brightest
                 surface on screen. It was the mass I twice mistook for the wall tile
                 and for scenery rock; hit-testing the pixels found it. Same warm
                 shift the rocks get, and it now peaks below full so the corridor
                 behind it stays legible. */
              filter: 'brightness(.6) saturate(.9) sepia(.2) hue-rotate(-8deg)',
              opacity: Math.max(0, 1 - depth / ENTRANCE_FADE_M) * 0.72,
            }}
            aria-hidden
          />
        )}

        {/* the miner, small and centred, seen from behind */}
        <div
          className="pointer-events-none absolute bottom-[6%] left-1/2 z-[6] h-[26%] w-[18%]"
          style={{
            transform: `translate3d(-50%, ${walking && !focused ? -2 : 0}px, 0)`,
            WebkitTransform: `translate3d(-50%, ${walking && !focused ? -2 : 0}px, 0)`,
            willChange: 'transform',
          }}
        >
          <MinerBack walking={walking && !focused} swinging={swinging} />
        </div>

        <CaveDecor
          view={view}
          lightPx={lightPx}
          depth={depth}
          torchPct={torchPct}
          energyPct={energy.max > 0 ? (energy.current / energy.max) * 100 : 100}
          {...(zone.fog ? { fog: zone.fog } : {})}
        />

        <TorchLight
          lightPx={lightPx}
          torchPct={torchPct}
          energyPct={energy.max > 0 ? (energy.current / energy.max) * 100 : 100}
          depth={depth}
          brightness={brightness}
          {...(zone.fog ? { fog: zone.fog } : {})}
        />

        {/* Search the wall beside you — the only route to a hidden chest, a mystery
            door, or a layer of the map to the next cave. Mounted here rather than in
            the button bar because "the left wall" already has a place on screen. */}
        <WallSearch />
        <WallSearchCard />

        <span className="absolute left-2 top-2 z-[9] rounded-md border border-[var(--hairline)]
                         bg-black/60 px-1.5 py-1 font-pixel text-[6.5px] tracking-wide text-ink-dim">
          {zone.name}
        </span>

        <button
          type="button"
          onClick={() => { stopHold(); setPaused(true); }}
          className="absolute right-2 top-2 z-[9] rounded-full border border-[var(--hairline)]
                     bg-black/70 px-2.5 py-1.5 backdrop-blur"
          aria-label="Pause"
        >
          <Icon name="pause" size={15} />
        </button>

        {/* Something went wrong and the player deserves to know. */}
        {notice && (
          <div className="absolute inset-x-3 top-12 z-[11] flex items-start gap-2 rounded-md
                          border border-[rgba(224,85,63,.45)] bg-black/90 p-2.5 backdrop-blur">
            <span className="text-[#FF9A8B]" aria-hidden><Icon name="warning" size={15} /></span>
            <p role="status" className="flex-1 text-[11px] leading-snug text-ink">{notice}</p>
            <button type="button" onClick={dismissNotice}
                    className="font-pixel text-[7px] text-ink-dim" aria-label="Dismiss">
              <Icon name="close" size={11} />
            </button>
          </div>
        )}

        {/* An actionable gate that is not the lamp or the torch — out of energy,
            axe too weak for something the server refused. */}
        {blocker && !lampBlocked && (
          <div className="absolute inset-x-3 top-12 z-[10] rounded-md border border-ui-lit
                          bg-black/85 p-2.5 text-center backdrop-blur">
            <p className="text-[11px] leading-snug text-ink">{blocker.message}</p>
            <button type="button" onClick={clearBlocker}
                    className="mt-1.5 text-[11px] text-ink-dim underline">Dismiss</button>
          </div>
        )}

        {/* mock panel 5 — blocked, and panel 4 — too dark */}
        {(rock?.locked || lampBlocked || atLampCap || torchDead) && (
          <div className="absolute inset-x-3 bottom-3 z-[9] rounded-md border border-ui-lit
                          bg-black/85 p-3 text-center backdrop-blur">
            <h3 className="font-pixel text-[9px] text-[var(--warn)]">
              {(lampBlocked || atLampCap) ? 'DARKNESS AHEAD'
                : torchDead ? 'TORCH BURNED OUT'
                : 'ROCK BLOCKING YOUR PATH'}
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-ink">
              {(lampBlocked || atLampCap)
                ? `Your lamp only reaches ${capLabel} — this is as deep as it goes. `
                  + 'Surface to keep everything you dug up, then upgrade the '
                  + 'lamp to come back deeper.'
                : torchDead
                  ? 'Return to the surface to refuel, or watch an ad in the Reward Hub.'
                  : `This rock needs axe level ${rock?.required_axe_level}. `
                    + 'Walk forward to leave it behind.'}
            </p>
            {/*
              * The cap needs a BUTTON, not just an explanation.
              *
              * At the lamp's limit the only way onward is to surface, and the only
              * route to surfacing was the pause menu — so a run ended in a panel that
              * told you what to do and gave you no way to do it. That is what "you can
              * only play once" means in practice: the loop had no visible exit back
              * into itself.
              *
              * Same for a dead torch. Both are ends of a run, and the end of a run
              * should offer the next one.
              */}
            {(atLampCap || torchDead) && (
              <button
                type="button"
                onClick={surface}
                className="mt-2.5 w-full rounded-sm border border-[rgba(255,220,170,.5)]
                           bg-[linear-gradient(#FFA945,#E0761C)] py-2 font-bold
                           text-[#2A1503] active:translate-y-0.5"
              >
                Surface and bank your finds
              </button>
            )}
            {lampBlocked && (
              <button type="button" onClick={clearBlocker}
                      className="mt-2 text-[11px] text-ink-dim underline">Dismiss</button>
            )}
          </div>
        )}

        {/* mock panel 6 — "Tap the rock to hit!" */}
        {rock && !rock.locked && phase === 'breaking' && (
          <p className="pointer-events-none absolute inset-x-0 bottom-3 z-[8] text-center
                        font-pixel text-[8px] tracking-wider text-ink-dim">
            {throttled ? 'SWINGING TOO FAST' : 'TAP THE ROCK TO HIT'}
          </p>
        )}
      </div>

      {/* The rock, full screen. Mounted only for a rock that can actually be hit —
          a locked one has its own panel explaining the axe requirement. */}
      {focused && rock && !rock.locked && (
        <RockFocus rock={rock} onClose={() => setFocused(false)} onSwung={swingFx} />
      )}

      {paused && (
        <PauseMenu
          onClose={() => setPaused(false)}
          onSurface={surface}
          onMaps={() => { setPaused(false); onMaps(); }}
        />
      )}

      {/* ── controls: pause · back · swing ── */}
      <div className="flex shrink-0 items-stretch gap-2 border-t border-[var(--hairline)]
                      bg-black/50 p-2.5 pb-[calc(env(safe-area-inset-bottom)+10px)]">
        <button
          type="button"
          onClick={() => { stopHold(); setPaused(true); }}
          className="grid w-14 place-items-center rounded-sm border border-ui-lit
                     bg-[linear-gradient(#3A2718,var(--cave-floor))] text-lg
                     shadow-[0_4px_0_-1px_rgba(0,0,0,.55)] active:translate-y-0.5"
          aria-label="Pause and open the menu"
        >
          <Icon name="pause" size={18} />
        </button>

        {/* Back up the tunnel.
            Deliberately narrow and unlit next to MOVE FORWARD: the game is a descent,
            and a back control with equal weight would read as though turning round were
            half the loop. It is an escape hatch — from a vein the axe cannot touch, and
            from having walked past a rock worth having. It costs the same fuel as a step
            forward and cannot mint depth; the server enforces both. */}
        <button
          type="button"
          onClick={doStepBack}
          disabled={cannotWalk || depth <= 0}
          className="grid w-14 place-items-center rounded-sm border border-ui-lit
                     bg-[linear-gradient(#3A2718,var(--cave-floor))] text-torch-core
                     shadow-[0_4px_0_-1px_rgba(0,0,0,.55)] active:translate-y-0.5
                     disabled:opacity-40 disabled:grayscale"
          aria-label="Walk back up the tunnel"
          style={{ touchAction: 'manipulation' }}
        >
          <span className="text-xl leading-none" aria-hidden>▼</span>
        </button>

        <button
          type="button"
          onPointerDown={startHold}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          disabled={cannotWalk}
          className="flex flex-1 select-none flex-col items-center justify-center gap-0.5 rounded-sm border
                     border-[rgba(255,220,170,.5)] bg-[linear-gradient(#FFA945,#E0761C)]
                     py-2.5 text-[#2A1503] shadow-[0_4px_0_-1px_#8C440C]
                     active:translate-y-0.5 disabled:opacity-40 disabled:grayscale"
          style={{ touchAction: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
        >
          <span className="text-xl leading-none" aria-hidden>▲</span>
          <span className="font-pixel text-[6.5px] tracking-wide">
            {torchDead ? 'TORCH OUT'
              : atLampCap ? `LAMP LIMIT ${capLabel}`
                : breakableInPath ? 'BREAK IT FIRST'
                  : lockedInPath ? 'WALK PAST IT'
                    : 'MOVE FORWARD'}
          </span>
        </button>

        {/* Opens the full-screen view, same as tapping the rock. Two controls that
            both mean "mine" must not do different things — this one used to swing in
            place, so a player could work a rock to nothing without ever seeing the
            damage they were doing. */}
        <button
          type="button"
          onClick={rock && !rock.locked ? () => setFocused(true) : undefined}
          disabled={!rock || rock.locked}
          className="grid w-14 place-items-center rounded-sm border border-ui-lit
                     bg-[linear-gradient(#3A2718,var(--cave-floor))] text-torch-core
                     shadow-[0_4px_0_-1px_rgba(0,0,0,.55)] active:translate-y-0.5
                     disabled:opacity-40 disabled:grayscale"
          aria-label="Mine this rock"
          style={{ touchAction: 'manipulation' }}
        >
          <Icon name="pick" size={22} />
        </button>
      </div>

      {/* ── In-Cave Quick Inventory & Balance Drawer ── */}
      <CaveBagDrawer isOpen={bagOpen} onClose={() => setBagOpen(false)} />

      {/* ── Subterranean Audio & Soundscape Controls Modal ── */}
      <SoundSettingsModal isOpen={soundOpen} onClose={() => setSoundOpen(false)} />
    </div>
  );
}
